import { TypographyColors } from "types/Colors";
import {
  RunObservabilitySeverity,
  RunWorkerMetrics,
  RunWorkerOutcome,
  RunWorkerOutcomeCategory,
} from "types/generated";
import { getDuration } from "utils/time";

import { RunObservabilityFinding } from "../Findings/types";
import { RunObservabilityStatTile, RunObservabilityStatTileComparison } from "./types";

const ratioFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 2,
  useGrouping: false,
});

const formatRunObservabilityRatio = (ratio: number) => `${ratioFormatter.format(ratio)}x`;

const DURATION_TOOLTIP =
  "Time the run spent on the worker. This may be lower than the actual run duration, as it excludes server-side overhead, including phases that happen outside the worker such as preparing and finalizing.";

const OUTCOME_CATEGORY_COLORS: Record<RunWorkerOutcomeCategory, TypographyColors> = {
  [RunWorkerOutcomeCategory.Healthy]: "success",
  [RunWorkerOutcomeCategory.Finished]: "warning",
  [RunWorkerOutcomeCategory.Slow]: "warning",
  [RunWorkerOutcomeCategory.Interrupted]: "danger",
  [RunWorkerOutcomeCategory.Failed]: "danger",
};

export const toRunObservabilityStatTiles = (
  workerMetrics: RunWorkerMetrics,
  findings: RunObservabilityFinding[],
  outcome: RunWorkerOutcome | null | undefined
): RunObservabilityStatTile[] => {
  const tiles: RunObservabilityStatTile[] = [];

  if (outcome?.category) {
    tiles.push({
      id: "outcome",
      label: "Outcome",
      value: outcome.title,
      valueColor: OUTCOME_CATEGORY_COLORS[outcome.category],
      tooltip: outcome.description ?? undefined,
    });
  }

  const { seconds, p95Ratio, p95Severity } = workerMetrics.duration;

  if (seconds) {
    let comparison: RunObservabilityStatTileComparison | undefined;

    if (p95Severity === RunObservabilitySeverity.None) {
      comparison = { text: "OK", severity: p95Severity };
    } else if (p95Severity && p95Ratio) {
      comparison = {
        text: `${formatRunObservabilityRatio(p95Ratio)} vs p95`,
        severity: p95Severity,
      };
    }

    const duration = getDuration(seconds, true, false) ?? "0s";

    tiles.push({
      id: "duration",
      label: "Duration",
      value: duration,
      tooltip: DURATION_TOOLTIP,
      comparison,
    });
  }

  const failedResources = workerMetrics.failedResources.count;

  if (failedResources > 0) {
    tiles.push({
      id: "failedResources",
      label: "Failed resources",
      value: String(failedResources),
      valueColor: "danger",
    });
  }

  tiles.push({
    id: "findings",
    label: "Findings",
    value: String(findings.length),
  });

  return tiles;
};
